import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  IconButton,
  Grid,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RetailerLookupButton from '../../general/retailerLookupButton.js';
import axios from 'axios';
import GameSelectModal from '../../general/GamesSelectModal.js';
import CustomerSurveyForm from '../../general/customerSurvey.js';
import ConfirmationModal from '../../general/ConfirmationModal.js';
import SubmissionSuccessModal from '../../general/SubmissionSuccessModal.js';
import njLogo from '../../../assets/nj-logo.png';
import { isValidRetailerNumber } from '../../../utils/isValidRetailerNumber.js';

const NewJerseyScratchOffsForm = () => {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    jurisdiction: 'New Jersey',
    retailerId: '',
    storeName: '',
    address: '',
    phone: '',
    name: '',
    comments: '',
    games: [],
  });
  const [cSatRetailerId, setCSatRetailerId] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [submittedGames, setSubmittedGames] = useState([]);
  const [isGameSelectModalOpen, setIsGameSelectModalOpen] = useState(false);
  const [isCustomerSurveyModalOpen, setIsCustomerSurveyModalOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (name === 'retailerId') {
      setCSatRetailerId(value);
    }
  };

  useEffect(() => {
    document.title = 'RSS - New Jersey Orders';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = njLogo;
  }, []);

  const handleConfirmSubmit = () => {
    setIsConfirmationModalOpen(false);
    handleSubmit();
  };

  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    setIsCustomerSurveyModalOpen(true);
  };

  const handleGameSelectModalOpenClose = () => {
    setIsGameSelectModalOpen(!isGameSelectModalOpen);
  };

  const handleCustomerSurveyModalOpenClose = () => {
    setIsCustomerSurveyModalOpen(!isCustomerSurveyModalOpen);
  };

  const handleClear = () => {
    setForm({
      ...form,
      retailerId: '',
      storeName: '',
      address: '',
      phone: '',
      name: '',
      comments: '',
      games: [],
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const isValidRetailer = await isValidRetailerNumber(
        form.retailerId,
        form.jurisdiction,
      );

      if (!isValidRetailer) {
        setSnackbarMessage(t('snackbar.invalid_retailer'));
        setSnackbarSeverity('error');
        setIsSnackbarOpen(true);
        setIsSubmitting(false);
        return;
      }

      const gamesWithQuantity = (form.games || []).filter(
        games => games.quantity != null && games.quantity !== '',
      );
      const payload = {
        ...form,
        games: gamesWithQuantity,
      };
      await axios({
        method: 'POST',
        url: process.env.REACT_APP_ENDPOINT_RSS_SUBMIT,
        data: payload,
      });
      setSubmittedGames(gamesWithQuantity);
      setIsSuccessModalOpen(true);
      setIsSubmitting(false);
      handleClear();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(t('snackbar.submit_error'));
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return (
      form.name &&
      form.retailerId &&
      form.storeName &&
      form.address &&
      form.games &&
      form.games.some(game => game.quantity != null && game.quantity !== '')
    );
  };

  return (
    <Box
      sx={{
        width: '75%',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowX: 'hidden',
        pb: { xs: 4, md: 0 },
      }}
    >
      <IconButton
        sx={{
          alignSelf: 'flex-start',
          marginBottom: 2,
          backgroundColor: 'var(--nj-secondary)',
          color: '#FFFFFF',
        }}
        size='large'
        onClick={() => navigate('/nj')}
      >
        <ArrowBackIcon />
      </IconButton>

      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: 'center',
          width: { xs: '100%', md: '75%' },
        }}
      >
        <Grid size={12}>
          <TextField
            required
            label={t('form.name')}
            name='name'
            fullWidth
            value={form.name}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
          <Tooltip title={t('tooltips.retailer_id_open')} arrow>
            <TextField
              label={t('form.retailer_id')}
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton
                        form={form}
                        setForm={setForm}
                        backgroundColor={'var(--nj-secondary)'}
                        color={'#FFFFFF'}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label={t('form.store_name')}
            name='storeName'
            required
            value={form.storeName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label={t('form.address')}
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label={t('form.phone_number')}
            name='phone'
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phone}
            onChange={handleChange}
            required
          />
          <Button
            variant={'contained'}
            disabled={!form.jurisdiction}
            onClick={handleGameSelectModalOpenClose}
            sx={{
              backgroundColor: '#7C3AED',
              color: '#FFFFFF',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            {t('buttons.select_games')}
          </Button>
          <TextField
            label={t('form.comments')}
            name='comments'
            value={form.comments}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--nj-secondary)',
              color: '#FFFFFF',
            }}
            onClick={handleClear}
          >
            {t('buttons.clear')}
          </Button>
          <Tooltip
            title={!isFormValid() ? t('tooltips.submit_disabled') : ''}
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--nj-primary)',
                  color: '#000000',
                }}
                onClick={() => setIsConfirmationModalOpen(true)}
                loading={isSubmitting}
              >
                {t('buttons.submit')}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleConfirmSubmit}
        games={form.games}
        bgColor='var(--nj-secondary)'
        accentColor='var(--nj-primary)'
      />
      <SubmissionSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        games={submittedGames}
        bgColor='var(--nj-secondary)'
        accentColor='var(--nj-primary)'
      />
      {isGameSelectModalOpen && (
        <GameSelectModal
          isOpen={isGameSelectModalOpen}
          handleClose={handleGameSelectModalOpenClose}
          setForm={setForm}
          games={form.games}
          jurisdiction={form.jurisdiction}
          bgColor='var(--nj-secondary)'
          accentColor='var(--nj-primary)'
          modalTitle={t('modal.scratch_offs_games')}
        />
      )}
      {isCustomerSurveyModalOpen && (
        <CustomerSurveyForm
          isOpen={isCustomerSurveyModalOpen}
          handleClose={handleCustomerSurveyModalOpenClose}
          setIsSnackbarOpen={setIsSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
          retailerId={cSatRetailerId}
          jurisdiction={form.jurisdiction}
          bgColor='var(--nj-secondary)'
          accentColor='var(--nj-primary)'
        />
      )}
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewJerseyScratchOffsForm;
